import * as React from 'react';
import { useStore } from 'react-redux';
import { Link } from 'react-router-dom';
import { PageBody } from '../../../../components/page-components/PageBody';
import { PageContent } from '../../../../components/page-components/PageContent';
import { PagedList } from '../../../../components/paged-list/PagedList';
import { useScrollMemory } from '../../../../hooks/use-scroll-memory';
import { useSearchParams } from '../../../../hooks/use-search-params';
import { PagedApiResult } from '../../../../models/utilities/paged-request';
import { StatusCode } from '../../../../utils/api';
import { PageNames, Routes, ScrollStatePaths } from '../../../../utils/app-constants';
import { navigateToPage } from '../../../../utils/history';
import * as transportActions from '../../../../transportation/actions';
import { FleetActionEditAsset } from '../components/FleetActionEditAsset';
import { AssetStatus } from '../../../../transportation/models/enums/asset-status';
import { AssetTypeMetadata } from '../../../../transportation/models/enums/asset-type';
import { AssetSearchFilters } from '../../../../transportation/models/filters/asset-search-filters';
import { AssetRead } from '../../../../transportation/models/fleet/asset-read';
import { getAssetSearchFilters } from '../../../../transportation/selectors';
import { TransportationSvc } from '../../../../transportation/service';

interface IProps {
    resetFlag: any;
    createFlag: number;
}

export const AssetSetupScene = (props : IProps) => {

    const store                                 = useStore();
    const [service]                             = React.useState(new TransportationSvc());
    const [showInfoFlyout, setShowInfoFlyout]   = React.useState(false);
    const [editAsset, setEditAsset]             = React.useState(new AssetRead());

    const [filterParams, setFilterParams] = React.useState(() => {
        return getAssetSearchFilters(store.getState());
    });

    const [assets, setAssets] = React.useState(() => {
        const initial = new PagedApiResult<AssetRead>();
        initial.pagination = filterParams.paginationInfo;
        initial.pagination.reusePaging = true;
        return initial;
    });

    const scrollTo = useScrollMemory(
        ScrollStatePaths.FLEET_ASSETS,
        Routes.SHIPPING_FLEET_ASSETS_EDIT
    );
    
    useSearchParams(
        Routes.SHIPPING_FLEET_ASSETS_EDIT,
        transportActions.updateAssetSearchFilters(filterParams),
        transportActions.clearAssetSearch()
    );
    
    React.useEffect(() => {
        if (props.createFlag > 0) {
            setShowInfoFlyout(true);
        }
    }, [props.createFlag]);
    
    /**
     * OnSearch handler
     * @param selectedSearchIdx The index of the search param
     * @param params The search param object
     * @param pagingInfo The pagination info from the table
     */
    const onSearch = async (filters: AssetSearchFilters) => {
        const response = await service.search(AssetRead, filters);
        if (response.status === StatusCode.Success) {
            setAssets(response.data);
            scrollTo();
        }

        setFilterParams(filters);
    };

    const onParamsUpdate = (itm : any) => {
        setFilterParams(itm);
    };

    const onParamsReset = () => {
        const tmp = new AssetSearchFilters();
        setFilterParams(tmp);
    };

    const onFlyoutClose = async () => {
        props.resetFlag();
        setShowInfoFlyout(false);        
    };

    const onFlyoutSuccess = async (result : number) => {
        props.resetFlag();
        setEditAsset(new AssetRead());
        setShowInfoFlyout(false);

        // For now we will navigate to the newly created pickup.
        // Could be revisited later with a call to search
        // await onSearch(filterParams);
        // onSearch(filterParams);
        navigateToPage(Routes.SHIPPING_FLEET_ASSETS_EDIT_WITH_ID.formattify(result));
    };

    return (<PageContent pageName={PageNames.TRANSPORTATION_FLEET_ASSETS}>
            <PageBody>
                <PagedList
                    noResultsMessage="No assets found, please search again."
                    searchCallback={ onSearch }
                    data={ assets }
                    filterProps={ filterParams }
                    onFilterUpdate={ onParamsUpdate }
                    selectableClass=""
                    onReset={ onParamsReset }
                    rowRenderer={(asset: AssetRead) => 
                        <tr key={ asset.id }>
                            <td>{ asset.id }</td>
                            <td>
                                <Link to={Routes.SHIPPING_FLEET_ASSETS_EDIT_WITH_ID.formattify(asset.id)}>
                                    { asset.name }
                                </Link>
                            </td>
                            <td>{ AssetTypeMetadata[asset.type].display }</td>
                            <td>{ asset.serviceCenterId }</td>
                            <td>
                                { 
                                    asset.status === AssetStatus.Active 
                                        ? <span className="asset-status active">ACTIVE</span>
                                        : <span className="asset-status inactive">INACTIVE</span>
                                }
                            </td>
                        </tr>
                    }
                />  

                <FleetActionEditAsset
                    visible={ showInfoFlyout }
                    onSuccess={ onFlyoutSuccess }
                    onClose={ onFlyoutClose }
                    asset={ editAsset }
                />

            </PageBody>
        </PageContent>);
    
};
